import { useState } from "react";

import type { IEmployeeLocationFilterDto } from "app/pages/sales-intelligence/domain/contacts/types/filter-values";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import { getSelectedCodes } from "app/pages/sales-intelligence/utils/location";

import type { EmployeeLocationFilterOptions } from "../types/dto";

type UseEmployeeLocationFilterParams = {
    options: EmployeeLocationFilterOptions;
    value: IEmployeeLocationFilterDto | undefined;
    onFilterUpdate: (value: IEmployeeLocationFilterDto) => void;
};

export const useEmployeeLocationFilter = ({
    options,
    value,
    onFilterUpdate,
}: UseEmployeeLocationFilterParams): {
    inclusionValue: InclusionOption;
    regions: EmployeeLocationFilterOptions["regions"];
    countries: EmployeeLocationFilterOptions["countries"];
    handleInclusionSelection: (type: InclusionOption) => void;
    handleLocationChange: (codes: readonly number[]) => void;
} => {
    const [inclusionValue, setInclusionValue] = useState<InclusionOption>(
        value?.inclusion ?? inclusionOptionMap.includeOnly,
    );

    const { regions, countries } = options;

    const updateFilter = (partial: Partial<IEmployeeLocationFilterDto>) => {
        const expandedCodes = [...getSelectedCodes(value, regions)];
        onFilterUpdate({ ...value, codes: expandedCodes, ...partial, regions: [] });
    };

    const handleInclusionSelection = (type: InclusionOption) => {
        setInclusionValue(type);
        if (value?.codes?.length || value?.regions?.length) {
            updateFilter({ inclusion: type });
        }
    };

    const handleLocationChange = (codes: readonly number[]) => {
        updateFilter({ codes: [...codes], inclusion: inclusionValue });
    };

    return {
        inclusionValue,
        regions,
        countries,
        handleInclusionSelection,
        handleLocationChange,
    };
};
